import { useRef, useState, MouseEvent } from 'react';
import { BaseSelect } from '../BaseSelect/BaseSelect';
import { SelectOption, SelectProps } from './Select.types';
import { GMMenuItem } from '..';
import css from './Select.module.scss';
import classNames from 'classnames';
import { BaseClearIconButton } from '../BaseClearIconButton/BaseClearIconButton';
import { BaseArrowDropDown } from '../BaseArrowDropDown/BaseArrowDropDown';
import { BaseArrowDropDownProps } from '../BaseArrowDropDown/BaseArrowDropDown.types';
import { SelectChangeEvent } from '@mui/material';
import { BaseInputLabel } from '../BaseInputLabel/BaseInputLabel';
import { GMFormControl, GMFormWrapper, GMErrorMessage } from '../../Form';

/**
 * The uncontrolled `Select` control for implementations that
 * do not use Formik. This control's state must be maintained by the
 * component that it is implemented in.
 *
 * @param id - {@link SelectProps.id id} description.
 * @param name - {@link SelectProps.name name} description.
 * @param label - {@link SelectProps.label label} description.
 * @param isAdminSelect - {@link SelectProps.isAdminSelect isAdminSelect} description. (default: `false`)
 * @param selectRootClass - {@link SelectProps.selectRootClass selectRootClass} description.
 * @param menuItemRootClass - {@link SelectProps.menuItemRootClass menuItemRootClass} description.
 * @param value - {@link SelectProps.value value} description.
 * @param error - {@link SelectProps.error error} description. (default: `false`)
 * @param errorMessage - {@link SelectProps.errorMessage errorMessage} description.
 * @param items - {@link SelectProps.items items} description.
 * @param clearable - {@link SelectProps.clearable clearable} description. (default: `false`)
 * @param onChange - {@link SelectProps.onChange onChange} description.
 */
const Select = ({
    id,
    name,
    label,
    isAdminSelect = false,
    selectRootClass,
    menuItemRootClass,
    value,
    error = false,
    errorMessage,
    items,
    clearable = false,
    onChange,
}: SelectProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState<boolean>(false);

    return (
        <GMFormWrapper fullWidth>
            <GMFormControl>
                {label ? (
                    <BaseInputLabel
                        id={id}
                        classes={{
                            root: classNames(css.label, isAdminSelect ? css.adminFormLabel : css.formLabel),
                            focused: css.focused,
                            error: css.error,
                        }}
                        children={label}
                    />
                ) : null}
                <BaseSelect
                    inputRef={inputRef}
                    labelId={id}
                    classes={{
                        root: classNames(css.input, selectRootClass),
                        error: css.error,
                        select: css.baseInput,
                    }}
                    name={name}
                    value={value ?? ''}
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    onChange={(event: SelectChangeEvent<unknown>) => {
                        const selected: SelectOption | undefined = items.find((item: SelectOption) => {
                            return item.value === event.target.value;
                        });

                        if (selected !== undefined) {
                            onChange(selected.value);
                        }
                    }}
                    error={error}
                    IconComponent={clearable ? ({
                        className,
                    }: BaseArrowDropDownProps) => {
                        return (
                            <span className={css.inputEndAdornmentWrapper}>
                                {!!value === true ? (
                                    <BaseClearIconButton
                                        classes={{
                                            root: css.endAdornmentIconButton,
                                        }}
                                        onClick={() => onChange('')}
                                        size={'large'}
                                    />
                                ) : null}
                                <span
                                    className={css.selectArrowContainer}
                                    onClick={(_event: MouseEvent<HTMLSpanElement>) => {
                                        setOpen(true);

                                        if (inputRef.current) {
                                            inputRef.current.focus();
                                        }
                                    }}
                                    aria-hidden={true}
                                >
                                    <BaseArrowDropDown className={classNames(className, css.selectArrow)} />
                                </span>
                            </span>
                        );
                    } : undefined}
                >
                    {items.map((item: SelectOption, index: number) => {
                        return (
                            <GMMenuItem
                                className={menuItemRootClass}
                                key={`${id}-${index}`}
                                value={item.value}
                            >
                                {item.label}
                            </GMMenuItem>
                        );
                    })}
                </BaseSelect>
                <GMErrorMessage error={error}>
                    {errorMessage}
                </GMErrorMessage>
            </GMFormControl>
        </GMFormWrapper>
    );
};

export default Select;